import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { UPDATE_CONVERSATION_MUTATION } from "./ticketQueries";
import {
  conversationOptimisticResponse,
  type ConversationChange,
} from "./ticketHelpers";

type OptimisticArgs = Parameters<typeof conversationOptimisticResponse>;

// When this tab last changed a ticket's owner, by conversation id: the owner change that
// comes back live is then this tab's own and needs no toast (useOwnershipToasts).
// Module-level on purpose, one per tab: written and read on the main thread only.
const ownAssignments = new Map<string, number>();
const OWN_ASSIGNMENT_WINDOW_MS = 10_000;

/** Whether this tab changed the ticket's owner in the last few seconds. */
export function assignedInThisTab(conversationId: string): boolean {
  const at = ownAssignments.get(conversationId);
  return at !== undefined && Date.now() - at < OWN_ASSIGNMENT_WINDOW_MS;
}

/**
 * The header's status, priority and assignee changes. They are optimistic: the new value shows
 * at once and Apollo rolls it back when the mutation fails. A rollback alone would be a silent
 * snap-back (a deleted team, an expired token, no network), so a failure also says so in a toast
 * and the rejection never goes unhandled. The returned promise says whether the change landed.
 */
export function useConversationChange(
  agents: OptimisticArgs[2],
  teams: OptimisticArgs[3],
  currentUserId?: OptimisticArgs[4],
) {
  const { t } = useTranslation();
  const [updateConversation] = useMutation(UPDATE_CONVERSATION_MUTATION, {
    refetchQueries: ["Ticket", "GetTickets", "GetTicketsPane"],
  });
  return (
    conversation: OptimisticArgs[0],
    change: ConversationChange,
  ): Promise<boolean> => {
    if (change.agentId !== undefined) {
      ownAssignments.set(conversation.id, Date.now());
    }
    return updateConversation({
      variables: { conversationId: conversation.id, ...change },
      optimisticResponse: conversationOptimisticResponse(
        conversation,
        change,
        agents,
        teams,
        currentUserId,
      ),
    }).then(
      () => true,
      () => {
        toast.error(t("tickets.meta.updateFailed"));
        return false;
      },
    );
  };
}
